import { requester } from '@ibgroup-tws-webapps/ratio.ui';
import { MDFARM_API_PATHS } from './api-paths';
import type {
  MdfarmEventContractMarketOpenInterestParams,
  MdfarmEventContractMarketOpenInterestResponse,
  MdfarmAggOpenInterestParams,
  MdfarmAggOpenInterestResponse,
  MdfarmEventContractBidAskResponse,
  OptionOpenInterestResult,
} from '@/types/mdfarm.types';

/**
 * Get event contract market open interest data
 */
const MARKET_OI_CHUNK_SIZE = 50;

export const getEventContractMarketOpenInterest = async (
  params: MdfarmEventContractMarketOpenInterestParams,
  usePublicApi = false,
): Promise<MdfarmEventContractMarketOpenInterestResponse> => {
  if (!params.ids || params.ids.length === 0) {
    throw new Error('At least one market ID is required');
  }

  const chunks: string[][] = [];
  for (let i = 0; i < params.ids.length; i += MARKET_OI_CHUNK_SIZE) {
    chunks.push(params.ids.slice(i, i + MARKET_OI_CHUNK_SIZE));
  }

  const responses = await Promise.all(
    chunks.map(async (chunkIds) => {
      const response = await requester.request(
        MDFARM_API_PATHS.EVENT_CONTRACT_MARKET_OPEN_INTEREST(chunkIds, usePublicApi),
        { method: 'GET' },
      );
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
      }
      return response.json() as Promise<MdfarmEventContractMarketOpenInterestResponse>;
    }),
  );

  return { results: responses.flatMap((r) => r.results) };
};

/**
 * Get aggregated open interest across multiple venues for event contracts.
 * Pass YES-side conid@exchange pairs only (call side is used for aggregation).
 */
export const getAggOpenInterest = async (
  params: MdfarmAggOpenInterestParams,
  usePublicApi = false,
): Promise<MdfarmAggOpenInterestResponse> => {
  if (!params.conidExPairs || params.conidExPairs.length === 0) {
    throw new Error('At least one conidEx pair is required');
  }

  const response = await requester.request(
    MDFARM_API_PATHS.EVENT_CONTRACT_AGG_OPEN_INTEREST(params.conidExPairs, usePublicApi),
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get event contract bid-ask data for a single contract
 */
export const getEventContractBidAsk = async (
  conid: number,
  exchange: string,
  usePublicApi = false,
  fetchInit?: Pick<RequestInit, 'signal' | 'priority'>,
): Promise<MdfarmEventContractBidAskResponse> => {
  if (!conid) {
    throw new Error('conid is required');
  }

  const response = await requester.request(MDFARM_API_PATHS.EVENT_CONTRACT_BID_ASK(conid, exchange, usePublicApi), {
    ...fetchInit,
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get best bid-ask across multiple exchanges for a contract group
 */
export const getFlexbestBidAsk = async (
  conidExPairs: string[],
  usePublicApi = false,
  fetchInit?: Pick<RequestInit, 'signal' | 'priority'>,
): Promise<MdfarmEventContractBidAskResponse> => {
  if (!conidExPairs.length) {
    throw new Error('At least one conidEx pair is required');
  }

  const response = await requester.request(
    MDFARM_API_PATHS.EVENT_CONTRACT_FLEXBEST_BID_ASK(conidExPairs, usePublicApi),
    { ...fetchInit, method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get event contract option open interest data for a single contract
 * Uses public or private endpoint based on auth state
 */
export const getEventContractOptionOpenInterest = async (
  conid: number,
  exchange: string,
  usePublicApi = false,
  fetchInit?: Pick<RequestInit, 'signal' | 'priority'>,
): Promise<OptionOpenInterestResult> => {
  if (!conid) {
    throw new Error('conid is required');
  }

  const response = await requester.request(
    MDFARM_API_PATHS.EVENT_CONTRACT_OPEN_INTEREST(conid, exchange, usePublicApi),
    {
      ...fetchInit,
      method: 'GET',
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};
